import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { GET, POST, PUT, DELETE } from "../../services/api";

// Define the async thunk for adding an author
export const addAuthor = createAsyncThunk(
  "data/add/author",
  async (data, { rejectWithValue }) => {
    try {
      const response = await POST("/admin/author", data);
      if (response?.status === 200) {
        return response.data.result;
      } else {
        return rejectWithValue(response?.data || "Something went wrong");
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Failed to fetch data");
    }
  }
);

export const viewAuthor = createAsyncThunk(
  "data/view/author",
  async (authorId, { rejectWithValue }) => {
    try {
      const response = await GET(`/admin/author/view`, { author_id: authorId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || "An error occurred");
    }
  }
);

// Define the async thunk for listing authors
export const listAuthors = createAsyncThunk(
  "data/list/author",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await GET("/admin/author", { ...params });
      if (response?.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response?.data || "Something went wrong");
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Failed to fetch data");
    }
  }
);

// Define the async thunk for updating an author
export const updateAuthor = createAsyncThunk(
  "data/update/author",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await PUT(`/admin/author?author_id=${id}`, data);
      if (response?.status === 200) {
        return response.data.result;
      } else {
        return rejectWithValue(response?.data || "Something went wrong");
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Failed to update data");
    }
  }
);

// Define the async thunk for deleting an author
export const deleteAuthor = createAsyncThunk(
  "data/delete/author",
  async (id, { rejectWithValue }) => {
    try {
      const response = await DELETE(`/admin/author`, { author_id: id });
      if (response?.status === 200) {
        return response.data.result;
      } else {
        return rejectWithValue(response?.data || "Something went wrong");
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Failed to delete data");
    }
  }
);

const AuthorSlice = createSlice({
  name: "author",
  initialState: {
    addAuthorData: [],
    addAuthorLoading: false,
    addAuthorError: null,
    listAuthorData: [],
    listAuthorCount: "",
    listAuthorLoading: false,
    listAuthorError: null,
    viewAuthorData: [],
    viewAuthorLoading: false,
    viewAuthorError: null,
    updateAuthorData: [],
    updateAuthorLoading: false,
    updateAuthorError: null,
    deleteAuthorData: [],
    deleteAuthorLoading: false,
    deleteAuthorError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addAuthor.pending, (state) => {
        state.addAuthorLoading = true;
        state.addAuthorError = null;
      })
      .addCase(addAuthor.fulfilled, (state, action) => {
        state.addAuthorLoading = false;
        state.addAuthorData = action.payload;
      })
      .addCase(addAuthor.rejected, (state, action) => {
        state.addAuthorLoading = false;
        state.addAuthorError = action.error.message;
      })

      .addCase(viewAuthor.pending, (state) => {
        state.viewAuthorLoading = true;
        state.viewAuthorError = null;
      })
      .addCase(viewAuthor.fulfilled, (state, action) => {
        state.viewAuthorLoading = false;
        state.viewAuthorData = action.payload.result;
      })
      .addCase(viewAuthor.rejected, (state, action) => {
        state.viewAuthorLoading = false;
        state.viewAuthorError = action.error.message;
      })

      .addCase(listAuthors.pending, (state) => {
        state.listAuthorLoading = true;
        state.listAuthorError = null;
      })
      .addCase(listAuthors.fulfilled, (state, action) => {
        state.listAuthorLoading = false;
        state.listAuthorData = action.payload?.result?.rows;
        state.listAuthorCount = action.payload?.result?.count;
      })
      .addCase(listAuthors.rejected, (state, action) => {
        state.listAuthorLoading = false;
        state.listAuthorError = action.error.message;
      })

      .addCase(updateAuthor.pending, (state) => {
        state.updateAuthorLoading = true;
        state.updateAuthorError = null;
      })
      .addCase(updateAuthor.fulfilled, (state, action) => {
        state.updateAuthorLoading = false;
        state.updateAuthorData = action.payload;
      })
      .addCase(updateAuthor.rejected, (state, action) => {
        state.updateAuthorLoading = false;
        state.updateAuthorError = action.error.message;
      })

      .addCase(deleteAuthor.pending, (state) => {
        state.deleteAuthorLoading = true;
        state.deleteAuthorError = null;
      })
      .addCase(deleteAuthor.fulfilled, (state, action) => {
        state.deleteAuthorLoading = false;
        state.deleteAuthorData = action.payload;
      })
      .addCase(deleteAuthor.rejected, (state, action) => {
        state.deleteAuthorLoading = false;
        state.deleteAuthorError = action.error.message;
      });
  },
});

export default AuthorSlice.reducer;
